import client from '../../shared/api/client';
import {
  P2pRate,
  P2pRatePreviewResult,
  PaginatedResponse,
  P2pFeeType,
  P2pAppliesTo,
  P2pOfferType,
} from '../../shared/types';

interface RateBackendResponse<T> {
  status: string;
  data: T;
}

export interface RatesFilters {
  page?: number;
  per_page?: number;
  search?: string;
  currency?: string;
  fiat_currency?: string;
  is_active?: boolean;
  fee_type?: P2pFeeType;
  mode?: 'p2p' | 'otc';
}

export interface RateInput {
  mode?: 'p2p' | 'otc';
  name: string;
  description?: string | null;
  fee_type: P2pFeeType;
  applies_to: P2pAppliesTo;
  value?: number | string | null;
  maker_value?: number | string | null;
  taker_value?: number | string | null;
  min_fee?: number | string | null;
  max_fee?: number | string | null;
  currencies?: string[];
  fiat_currencies?: string[];
  countries?: string[];
  payment_method_ids?: number[];
  offer_type?: P2pOfferType | null;
  min_verification_level?: number;
  is_active?: boolean;
  effective_from?: string | null;
  effective_to?: string | null;
}

export interface RatePreviewInput {
  mode?: 'p2p' | 'otc';
  currency?: string;
  fiat_currency?: string;
  country?: string;
  payment_method_id?: number;
  offer_type?: P2pOfferType;
  verification_level?: number;
  total?: number | string;
}

export const listRates = async (filters: RatesFilters = {}): Promise<PaginatedResponse<P2pRate>> => {
  const params: Record<string, string | number | boolean | undefined> = {
    page: filters.page ?? 1,
    per_page: filters.per_page ?? 20,
  };
  if (filters.search) params.search = filters.search;
  if (filters.currency) params.currency = filters.currency;
  if (filters.fiat_currency) params.fiat_currency = filters.fiat_currency;
  if (filters.fee_type) params.fee_type = filters.fee_type;
  if (typeof filters.is_active === 'boolean') params.is_active = filters.is_active;
  if (filters.mode) params.mode = filters.mode;
  const response = await client.get<RateBackendResponse<PaginatedResponse<P2pRate>>>('/p2p/rates', { params });
  return response.data.data;
};

export const getRate = async (id: number): Promise<P2pRate> => {
  const response = await client.get<RateBackendResponse<P2pRate>>(`/p2p/rates/${id}`);
  return response.data.data;
};

export const createRate = async (data: RateInput): Promise<P2pRate> => {
  const response = await client.post<RateBackendResponse<P2pRate>>('/p2p/rates', data);
  return response.data.data;
};

export const updateRate = async (id: number, data: Partial<RateInput>): Promise<P2pRate> => {
  const response = await client.put<RateBackendResponse<P2pRate>>(`/p2p/rates/${id}`, data);
  return response.data.data;
};

export const deleteRate = async (id: number): Promise<void> => {
  await client.delete(`/p2p/rates/${id}`);
};

export const previewRate = async (input: RatePreviewInput): Promise<P2pRatePreviewResult> => {
  const response = await client.post<RateBackendResponse<P2pRatePreviewResult>>('/p2p/rates/preview', input);
  return response.data.data;
};
